import React, {useState, useRef, useEffect, useLayoutEffect} from 'react'
import {Box, useMultiStyleConfig} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import {getGDIHideStyles} from '../../utils/gdi-styles'
import {renderLayoutsSwitch, renderComponentsSwitch} from '../../../utils/contentRenderer'

const scrollActiveTabIntoView = (container, tab) => {
    if (!container || !tab) return
    const containerWidth = container.offsetWidth
    const maxScroll = Math.max(0, container.scrollWidth - containerWidth)
    // Clamp to [0, maxScroll] so the first tab never tries to animate to a
    // negative offset (which causes a visible flicker as the browser clamps it).
    const targetScrollLeft = Math.max(
        0,
        Math.min(maxScroll, tab.offsetLeft - containerWidth / 2 + tab.offsetWidth / 2)
    )
    const startScrollLeft = container.scrollLeft
    const distance = targetScrollLeft - startScrollLeft
    if (Math.abs(distance) < 1) return

    const duration = 300
    const startTime = performance.now()
    const easeOut = (t) => 1 - Math.pow(1 - t, 3)

    const animate = (now) => {
        const progress = Math.min((now - startTime) / duration, 1)
        container.scrollLeft = startScrollLeft + distance * easeOut(progress)
        if (progress < 1) requestAnimationFrame(animate)
    }
    requestAnimationFrame(animate)
}

const renderContentArea = (contentArea) => {
    if (!Array.isArray(contentArea) || contentArea.length === 0) return null
    return contentArea
        .map((item, i) => {
            const exp = item?.ContentLink?.Expanded
            if (!exp) return null
            return (
                <React.Fragment key={i}>
                    {renderLayoutsSwitch(exp) || renderComponentsSwitch(exp)}
                </React.Fragment>
            )
        })
        .filter(Boolean)
}

const GDITabsLayoutBlock = ({Tabs, HideBlockOn, GDITabsLayoutBlockStyling, LayoutStyling}) => {
    const [activeIndex, setActiveIndex] = useState(0)
    const [contentVisible, setContentVisible] = useState(true)
    const styles = useMultiStyleConfig('GDITabsLayoutBlock', {})
    const tabBarRef = useRef(null)
    const tabRefs = useRef([])
    const isFirstRender = useRef(true)
    const fadeTimer = useRef(null)

    const tabs = (Tabs ?? []).filter(Boolean)
    const hideStyles = getGDIHideStyles(HideBlockOn)

    // --- Styling from GDITabsLayoutBlockStyling (array, read first entry) ---
    const styling = Array.isArray(GDITabsLayoutBlockStyling) ? GDITabsLayoutBlockStyling[0] : null
    // TilesGap may be a number (e.g. 8) or a string (e.g. '8px')
    const rawGap = styling?.TilesGap
    const tilesGap = rawGap != null && rawGap !== '' ? (typeof rawGap === 'number' ? `${rawGap}px` : rawGap) : null
    const tabPadding = styling?.GDITabsLayoutBlockPadding ?? null
    const tabMargins = styling?.Margins ?? null
    const tabBorderBottom = styling?.BorderBottom ?? null

    // --- Layout from LayoutStyling (array, read first entry) ---
    const layout = Array.isArray(LayoutStyling) ? LayoutStyling[0] : null
    const isCarouselMode = layout?.DisplayType === 'Carousel'
    const tilesPerRow = isCarouselMode && layout?.TilesPerRow ? layout.TilesPerRow : null

    // When there are at most 3 tabs, spread the buttons evenly across the full
    // container width (max 352px so they don't stretch too wide on desktop).
    // Disabled in carousel mode — TilesPerRow controls the width instead.
    const stretchTabs = !isCarouselMode && tabs.length > 0 && tabs.length <= 3

    // In carousel mode with TilesPerRow: each tab occupies 1/N of the container.
    const tabFixedWidth = tilesPerRow ? `${100 / tilesPerRow}%` : null

    // In carousel mode without TilesPerRow: spread tabs evenly with space-between
    // so images render at their natural size and the row fills the container.
    // flexShrink:0 (from styles.tab) prevents compression; overflowX:auto on
    // tabBar handles scrolling when images are wider than the container.
    const carouselStretch = isCarouselMode && !tilesPerRow

    // Reset scroll position to 0 synchronously before the first paint so the
    // tab bar never briefly appears in a non-zero scroll state (browser scroll
    // restoration can leave overflowing flex containers mid-scroll on revisit).
    useLayoutEffect(() => {
        if (tabBarRef.current) tabBarRef.current.scrollLeft = 0
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    // On mount: detect URL hash and set initial active index.
    // No need to scroll to tab 0 — useLayoutEffect already guarantees scrollLeft = 0.
    useEffect(() => {
        const hash = typeof window !== 'undefined' ? window.location.hash : ''
        if (hash) {
            const match = hash.match(/^#tab-(\d+)$/)
            if (match) {
                const index = parseInt(match[1], 10)
                if (index > 0 && index < tabs.length) {
                    setActiveIndex(index)
                }
            }
        }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    // Scroll active tab into view on tab change.
    // Skip the very first run: the tab bar starts at scrollLeft = 0 (set by
    // useLayoutEffect), so no animation is needed — and firing scrollActiveTabIntoView
    // before the container has been laid out can produce a wrong targetScrollLeft
    // (e.g. containerWidth = 0 → target = tabWidth/2 > 0) which cuts the first tab.
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false
            return
        }
        const raf = requestAnimationFrame(() =>
            scrollActiveTabIntoView(tabBarRef.current, tabRefs.current[activeIndex])
        )
        return () => cancelAnimationFrame(raf)
    }, [activeIndex])

    // Clear fade timer on unmount
    useEffect(() => () => clearTimeout(fadeTimer.current), [])

    if (hideStyles.doNotLoadComponent) return null
    if (tabs.length === 0) return null

    const activeTab = tabs[activeIndex]

    const handleTabChange = (i) => {
        if (i === activeIndex) return
        clearTimeout(fadeTimer.current)
        setContentVisible(false)
        fadeTimer.current = setTimeout(() => {
            setActiveIndex(i)
            setContentVisible(true)
        }, 130)
    }

    return (
        <Box data-block-type="GDITabsLayoutBlock" sx={hideStyles.sx} __css={styles.container}>
            {/* Tab buttons — scrollable when overflow (carousel) */}
            <Box ref={tabBarRef} __css={styles.tabBar} {...(tabMargins && {margin: tabMargins})}>
                <Box
                    __css={styles.tabList}
                    {...(tilesGap && {gap: tilesGap})}
                    {...(stretchTabs && {width: '100%', maxWidth: '352px'})}
                    {...(carouselStretch && {width: '100%', justifyContent: 'space-between', alignItems: 'flex-start'})}
                    {...(isCarouselMode && tabFixedWidth && {alignItems: 'flex-start'})}
                >
                        {tabs.map((tab, i) => {
                            const isActive = i === activeIndex
                            const label =
                                tab.TextOnly ||
                                renderContentArea(tab.CustomContent) ||
                                null
                            return (
                                <Box
                                    key={i}
                                    ref={(el) => {
                                        tabRefs.current[i] = el
                                    }}
                                    as="button"
                                onClick={() => handleTabChange(i)}
                                __css={styles.tab}
                                {...(stretchTabs && {flex: 1})}
                                {...(tabFixedWidth && {flexShrink: 0, width: tabFixedWidth})}
                                {...(tabPadding && {p: tabPadding})}
                                borderBottom={isActive ? (tabBorderBottom || '1px solid') : '1px solid transparent'}
                                borderColor={isActive ? 'webTheme.strokeDefault' : 'transparent'}
                            >
                                {label}
                            </Box>
                        )
                    })}
                </Box>
            </Box>

            {/* Active tab content — fades in/out on tab switch */}
            {activeTab && (
                <Box
                    __css={styles.content}
                    opacity={contentVisible ? 1 : 0}
                    transform={contentVisible ? 'translateY(0)' : 'translateY(4px)'}
                    transition="opacity 130ms ease, transform 130ms ease"
                >
                    {renderContentArea(activeTab.TabContent)}
                </Box>
            )}
        </Box>
    )
}

GDITabsLayoutBlock.displayName = 'GDITabsLayoutBlock'

GDITabsLayoutBlock.propTypes = {
    Tabs: PropTypes.arrayOf(
        PropTypes.shape({
            TextOnly: PropTypes.string,
            CustomContent: PropTypes.arrayOf(PropTypes.object),
            TabContent: PropTypes.arrayOf(PropTypes.object)
        })
    ),
    HideBlockOn: PropTypes.string,
    GDITabsLayoutBlockStyling: PropTypes.arrayOf(
        PropTypes.shape({
            TilesGap: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            GDITabsLayoutBlockPadding: PropTypes.string,
            Margins: PropTypes.string,
            BorderBottom: PropTypes.string
        })
    ),
    LayoutStyling: PropTypes.arrayOf(
        PropTypes.shape({
            DisplayType: PropTypes.string,
            TilesPerRow: PropTypes.number
        })
    )
}

export default GDITabsLayoutBlock
